import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  MessageSquare,
  Megaphone,
  ImageIcon,
  Layers,
  Film,
  Code2,
  Mic,
  Clapperboard,
  PlaySquare,
  Box,
  ArrowRight,
  Sparkles,
  Loader2,
  Wand2,
  Share2,
  Link2,
  Mail,
  MessageCircle,
  ExternalLink,
  Music2,
  Cuboid,
  Smartphone,
} from "lucide-react";
import Logo from "../components/Logo.jsx";
import AdUnit from "../components/AdUnit.jsx";
import SpeechToTextButton, {
  appendTranscript,
} from "../components/SpeechToTextButton.jsx";
import { routePrompt } from "../lib/taskRouter.js";
import { setPendingTask } from "../lib/pendingTask.js";
import { useI18n } from "../lib/i18n.jsx";

const FEATURES = [
  {
    to: "/chat",
    icon: MessageSquare,
    titleKey: "home.feature.chat.title",
    descKey: "home.feature.chat.desc",
    accent: "from-consensus-500/30 to-consensus-500/5",
  },
  {
    to: "/marketing",
    icon: Megaphone,
    titleKey: "home.feature.marketing.title",
    descKey: "home.feature.marketing.desc",
    accent: "from-emerald-500/30 to-consensus-500/5",
  },
  {
    to: "/image",
    icon: ImageIcon,
    titleKey: "home.feature.image.title",
    descKey: "home.feature.image.desc",
    accent: "from-accent-500/30 to-accent-500/5",
  },
  {
    to: "/image-studio",
    icon: Layers,
    titleKey: "home.feature.imageStudio.title",
    descKey: "home.feature.imageStudio.desc",
    accent: "from-cyan-500/25 to-consensus-500/5",
  },
  {
    to: "/video",
    icon: Film,
    titleKey: "home.feature.video.title",
    descKey: "home.feature.video.desc",
    accent: "from-violet-500/30 to-violet-500/5",
    comingSoon: true,
  },
  {
    to: "/tts",
    icon: Mic,
    titleKey: "home.feature.tts.title",
    descKey: "home.feature.tts.desc",
    accent: "from-accent-400/30 to-violet-500/5",
  },
  {
    to: "/studio",
    icon: Clapperboard,
    titleKey: "home.feature.studio.title",
    descKey: "home.feature.studio.desc",
    accent: "from-consensus-500/30 to-accent-500/5",
  },
  {
    to: "/shorts",
    icon: PlaySquare,
    titleKey: "home.feature.shorts.title",
    descKey: "home.feature.shorts.desc",
    accent: "from-rose-500/25 to-orange-500/5",
  },
  {
    to: "/model3d",
    icon: Box,
    titleKey: "home.feature.model3d.title",
    descKey: "home.feature.model3d.desc",
    accent: "from-sky-500/25 to-consensus-500/5",
  },
  {
    to: "/code",
    icon: Code2,
    titleKey: "home.feature.code.title",
    descKey: "home.feature.code.desc",
    accent: "from-consensus-400/30 to-accent-500/5",
  },
];

const APP_STORE_URL =
  "https://apps.apple.com/us/app/consensus-a-i/id6743997699";

const OTHER_PRODUCTS = [
  {
    href: "https://lucidaudiostreaming.com/",
    icon: Music2,
    titleKey: "home.otherProducts.lucid.title",
    descKey: "home.otherProducts.lucid.desc",
    accent: "from-violet-500/30 to-accent-500/5",
  },
  {
    href: "https://aitext2cad.com/",
    icon: Cuboid,
    titleKey: "home.otherProducts.text2cad.title",
    descKey: "home.otherProducts.text2cad.desc",
    accent: "from-sky-500/25 to-consensus-500/5",
  },
  {
    href: "https://rna-code.com/",
    icon: Code2,
    titleKey: "home.otherProducts.rna.title",
    descKey: "home.otherProducts.rna.desc",
    accent: "from-consensus-400/30 to-emerald-500/5",
  },
];

export default function Home() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [request, setRequest] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [shareCopied, setShareCopied] = useState(false);
  const EXAMPLES = [
    "Explain quantum computing like I'm 12",
    "Generate an image of a cozy cabin in the snow at night",
    "Turn this YouTube video into a Top 5 Shorts format",
    "Open Image Studio so I can layer photos and add text",
    "Create an Instagram post and image for my bakery's summer promo",
    "Create a logo for a coffee shop called Morning Brew",
    "Write a React Native todo app with a dark theme",
    "Read aloud: Welcome to The Consensus A.I.",
    "Build a slideshow about sustainable energy in Video Studio",
    "Help me debug a Python function that sorts a list",
  ];
  const PROJECT_TEMPLATES = [
    {
      id: "code-web-landing",
      title: t("home.template.codeWeb.title", "Code: React Landing Page"),
      description: t(
        "home.template.codeWeb.desc",
        "Generate a polished website starter with hero, features, and CTA."
      ),
      image: "/templates/code-web-landing.svg",
      route: "/code",
      tool: "code",
      prompt:
        "Build a modern React landing page for an AI startup with hero section, feature cards, pricing teaser, and contact call-to-action.",
      params: { codeMode: "react-web", mode: "generate" },
    },
    {
      id: "code-native-tracker",
      title: t("home.template.codeNative.title", "Code: React Native Tracker App"),
      description: t(
        "home.template.codeNative.desc",
        "Generate a mobile app starter with list management and clean UI."
      ),
      image: "/templates/code-native-tracker.svg",
      route: "/code",
      tool: "code",
      prompt:
        "Build a React Native habit tracker app with add/edit habit, progress checkmarks, streak labels, and a clean dark theme.",
      params: { codeMode: "react-native", mode: "generate" },
    },
    {
      id: "marketing-launch",
      title: t("home.template.marketingLaunch.title", "Marketing: Product Launch"),
      description: t(
        "home.template.marketingLaunch.desc",
        "Create social copy + visual prompt for a new product announcement."
      ),
      image: "/templates/marketing-launch.svg",
      route: "/marketing",
      tool: "marketing",
      prompt:
        "Create a launch post for a new productivity app for small business teams. Include a high-conversion caption, hashtags, and image concept.",
      params: {},
    },
    {
      id: "marketing-local",
      title: t("home.template.marketingLocal.title", "Marketing: Local Business Promo"),
      description: t(
        "home.template.marketingLocal.desc",
        "Create a weekly promo campaign for a neighborhood business."
      ),
      image: "/templates/marketing-local-promo.svg",
      route: "/marketing",
      tool: "marketing",
      prompt:
        "Create a social post and matching image prompt for a local coffee shop's weekend special targeting young professionals and students.",
      params: {},
    },
  ];

  const shareUrl =
    typeof window !== "undefined"
      ? window.location.origin
      : "https://theconsensusai.com";
  const shareText =
    `${t("home.free", "100% free and unlimited A.I. access- no sign up, no subscription needed")}`;

  const preview = useMemo(() => {
    const trimmed = request.trim();
    if (trimmed.length < 4) return null;
    return routePrompt(trimmed);
  }, [request]);

  const submit = (text) => {
    const value = (text ?? request).trim();
    if (!value || submitting) return;

    const routed = routePrompt(value);
    if (!routed.ok) {
      setError(routed.error);
      return;
    }

    setError(null);
    setSubmitting(true);

    const task = {
      route: routed.route,
      tool: routed.tool,
      prompt: routed.prompt,
      rawPrompt: routed.rawPrompt,
      params: routed.params,
      autoRun: !routed.comingSoon,
    };

    setPendingTask(task);
    navigate(routed.route, { state: { pendingTask: task } });
    setSubmitting(false);
  };

  const launchTemplate = (tpl) => {
    const task = {
      route: tpl.route,
      tool: tpl.tool,
      prompt: tpl.prompt,
      rawPrompt: tpl.prompt,
      params: tpl.params || {},
      autoRun: true,
    };
    setPendingTask(task);
    navigate(tpl.route, { state: { pendingTask: task } });
  };

  const shareNow = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: "The Consensus A.I.",
          text: shareText,
          url: shareUrl,
        });
        return;
      } catch {
        // User cancelled or share failed; fallback options remain available.
      }
    }
    setShareOpen((v) => !v);
  };

  const copyShareLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setShareCopied(true);
      setTimeout(() => setShareCopied(false), 1600);
    } catch {
      setError("Could not copy share link. Please copy the URL manually.");
    }
  };

  const smsHref = `sms:&body=${encodeURIComponent(
    `${shareText} ${shareUrl}`
  )}`;
  const emailHref = `mailto:?subject=${encodeURIComponent(
    "Try The Consensus A.I."
  )}&body=${encodeURIComponent(`${shareText}\n\n${shareUrl}`)}`;
  const xHref = `https://twitter.com/intent/tweet?text=${encodeURIComponent(
    `${shareText} ${shareUrl}`
  )}`;
  const linkedinHref = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
    shareUrl
  )}`;

  return (
    <div className="px-6 md:px-10 py-10 md:py-16 max-w-6xl mx-auto">
      <section className="relative">
        <div className="absolute inset-0 -z-10 bg-grid-fade rounded-3xl" />
        <div className="flex flex-col items-center text-center gap-6">
          <Logo className="h-28 w-28 md:h-36 md:w-36 drop-shadow-[0_0_40px_rgba(58,91,255,0.55)]" />
          <h1 className="text-4xl md:text-6xl font-extrabold tracking-tight leading-[1.05]">
            {t("home.welcome", "Welcome to")}{" "}
            <span className="bg-brand-gradient bg-clip-text text-transparent">
              The Consensus A.I.
            </span>
          </h1>
          <p className="text-lg md:text-xl text-white/70 max-w-2xl leading-relaxed">
            {t(
              "home.hero",
              "Tell us what you want to make — chat, images, code, speech, YouTube Shorts, and more. We'll open the right tool and start working on it."
            )}
          </p>
          <p className="text-base md:text-lg font-bold text-consensus-200 max-w-2xl">
            {t(
              "home.free",
              "100% free and unlimited A.I. access- no sign up, no subscription needed"
            )}
          </p>
          <div className="w-full max-w-2xl">
            <button onClick={shareNow} className="btn-secondary">
              <Share2 className="h-4 w-4" />
              {t("home.tellFriend", "Tell a friend")}
            </button>
            {shareOpen && (
              <div className="mt-3 rounded-xl border border-white/10 bg-white/[0.03] p-3 flex flex-wrap gap-2 justify-center">
                <a href={smsHref} className="btn-ghost text-xs">
                  <MessageCircle className="h-3.5 w-3.5" />
                  {t("home.share.text", "Text")}
                </a>
                <a href={emailHref} className="btn-ghost text-xs">
                  <Mail className="h-3.5 w-3.5" />
                  {t("home.share.email", "Email")}
                </a>
                <a href={xHref} target="_blank" rel="noreferrer" className="btn-ghost text-xs">
                  <Share2 className="h-3.5 w-3.5" />
                  {t("home.share.x", "X Post")}
                </a>
                <a
                  href={linkedinHref}
                  target="_blank"
                  rel="noreferrer"
                  className="btn-ghost text-xs"
                >
                  <Share2 className="h-3.5 w-3.5" />
                  {t("home.share.linkedin", "LinkedIn")}
                </a>
                <button onClick={copyShareLink} className="btn-ghost text-xs">
                  <Link2 className="h-3.5 w-3.5" />
                  {shareCopied
                    ? t("home.share.copied", "Copied")
                    : t("home.share.copy", "Copy Link")}
                </button>
              </div>
            )}
          </div>
        </div>
      </section>

      <section className="mt-10 max-w-3xl mx-auto">
        <div className="card !p-0 overflow-hidden border-consensus-500/20 shadow-glow">
          <div className="px-4 py-3 border-b border-white/5 bg-consensus-500/10 flex items-center gap-2 text-sm font-medium text-consensus-200">
            <Sparkles className="h-4 w-4" />
            {t("home.ask", "What do you want to create?")}
          </div>
          <div className="p-4 space-y-3">
            <div className="relative">
              <textarea
                value={request}
                onChange={(e) => {
                  setRequest(e.target.value);
                  setError(null);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                    e.preventDefault();
                    submit();
                  }
                }}
                rows={3}
                placeholder={t(
                  "home.placeholder",
                  "e.g. Generate an image of a sunset, explain transformers, or build a React app…"
                )}
                className="textarea pr-28 text-sm md:text-base"
              />
              <div className="absolute left-2 bottom-2">
                <SpeechToTextButton
                  onTranscript={(text) =>
                    setRequest((prev) => appendTranscript(prev, text))
                  }
                  onError={(msg) => setError(msg)}
                />
              </div>
              <button
                onClick={() => submit()}
                disabled={!request.trim() || submitting}
                className="btn-primary absolute right-2 bottom-2 !py-2 !px-4 text-sm"
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    {t("home.starting", "Starting…")}
                  </>
                ) : (
                  <>
                    <Wand2 className="h-4 w-4" />
                    {t("home.go", "Go")}
                  </>
                )}
              </button>
            </div>

            {preview?.ok && request.trim().length >= 4 && (
              <div className="flex items-start gap-2 rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2.5 text-left">
                <Sparkles className="h-4 w-4 text-consensus-400 shrink-0 mt-0.5" />
                <div className="min-w-0">
                  <p className="text-sm text-white/85">
                    {t("home.opens", "Opens")}{" "}
                    <span className="font-semibold">{preview.label}</span>
                    {preview.comingSoon && (
                      <span className="text-amber-200/90">
                        {" "}
                        {t("home.comingSoon", "(coming soon)")}
                      </span>
                    )}
                  </p>
                  <p className="text-xs text-white/45 mt-0.5">{preview.description}</p>
                </div>
              </div>
            )}

            {error && (
              <p className="text-xs text-red-300">{error}</p>
            )}

            <div className="flex flex-wrap gap-2">
              {EXAMPLES.map((ex) => (
                <button
                  key={ex}
                  type="button"
                  onClick={() => {
                    setRequest(ex);
                    submit(ex);
                  }}
                  disabled={submitting}
                  className="text-left text-[11px] md:text-xs text-white/55 hover:text-white border border-white/10 hover:border-white/20 bg-white/[0.02] hover:bg-white/[0.05] rounded-full px-3 py-1.5 transition"
                >
                  {ex}
                </button>
              ))}
            </div>

            <p className="text-[10px] text-white/30 text-center">
              {t("home.tip", "Tip: Cmd/Ctrl + Enter to start")}
            </p>
          </div>
        </div>
      </section>

      <section className="mt-10 max-w-3xl mx-auto">
        <AdUnit
          slot={import.meta.env.VITE_ADSENSE_SLOT_HOME}
          className="card p-3"
        />
      </section>

      <section className="mt-14 md:mt-20">
        <h2 className="text-sm font-semibold uppercase tracking-widest text-white/40 mb-4 text-center">
          {t("home.pick", "Or pick a tool")}
        </h2>
        <div className="grid sm:grid-cols-2 gap-4">
          {FEATURES.map((f) => (
            <Link
              key={f.to}
              to={f.to}
              className="group relative card hover:border-white/20 transition overflow-hidden"
            >
              <div
                className={`absolute -top-20 -right-20 h-48 w-48 rounded-full blur-3xl bg-gradient-to-br ${f.accent} opacity-60 group-hover:opacity-100 transition`}
              />
              <div className="relative flex items-start gap-4">
                <div className="h-11 w-11 shrink-0 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center">
                  <f.icon className="h-5 w-5 text-white/90" />
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="text-lg font-semibold">
                      {t(f.titleKey, f.titleKey)}
                    </h3>
                    {f.comingSoon && (
                      <span className="text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-200 border border-amber-500/25">
                        {t("home.comingSoon", "(coming soon)").replace(/[()]/g, "")}
                      </span>
                    )}
                    <ArrowRight className="h-4 w-4 text-white/30 group-hover:text-accent-400 group-hover:translate-x-1 transition" />
                  </div>
                  <p className="text-white/60 text-sm mt-1 leading-relaxed">
                    {t(f.descKey, f.descKey)}
                  </p>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      <section className="mt-14 md:mt-20">
        <h2 className="text-sm font-semibold uppercase tracking-widest text-white/40 mb-4 text-center">
          {t("home.templates.title", "Project Templates")}
        </h2>
        <p className="text-center text-sm text-white/55 max-w-2xl mx-auto mb-5">
          {t(
            "home.templates.subtitle",
            "Pick a starter template for Code Editor or Marketing. We will open the tool and prefill everything."
          )}
        </p>
        <div className="grid sm:grid-cols-2 gap-4">
          {PROJECT_TEMPLATES.map((tpl) => (
            <button
              key={tpl.id}
              type="button"
              onClick={() => launchTemplate(tpl)}
              className="group text-left card overflow-hidden border-white/10 hover:border-white/25 transition"
            >
              <img
                src={tpl.image}
                alt={tpl.title}
                className="w-full h-36 object-cover rounded-lg border border-white/10"
              />
              <div className="mt-3">
                <h3 className="text-sm md:text-base font-semibold text-white group-hover:text-consensus-200 transition">
                  {tpl.title}
                </h3>
                <p className="text-xs md:text-sm text-white/60 mt-1 leading-relaxed">
                  {tpl.description}
                </p>
                <span className="inline-flex items-center gap-1 text-xs text-consensus-200 mt-2">
                  {t("home.templates.use", "Use template")}
                  <ArrowRight className="h-3.5 w-3.5" />
                </span>
              </div>
            </button>
          ))}
        </div>
      </section>

      <section className="mt-14 md:mt-20">
        <div className="relative card overflow-hidden border-consensus-500/25 hover:border-consensus-500/40 transition">
          <div className="absolute -top-24 -right-24 h-56 w-56 rounded-full blur-3xl bg-gradient-to-br from-consensus-500/30 to-accent-500/10 opacity-70" />
          <div className="relative flex flex-col md:flex-row md:items-center gap-6 p-6 md:p-8">
            <div className="h-14 w-14 shrink-0 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center">
              <Smartphone className="h-7 w-7 text-consensus-300" />
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="text-xl md:text-2xl font-bold text-white">
                {t("home.mobileApp.title", "Try our mobile app")}
              </h2>
              <p className="text-sm md:text-base text-white/65 mt-2 leading-relaxed max-w-2xl">
                {t(
                  "home.mobileApp.desc",
                  "Get Consensus A.I. on iPhone and iPad — AI chat with text and speech input, text and audio responses, and support for top models."
                )}
              </p>
            </div>
            <a
              href={APP_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary shrink-0 inline-flex items-center gap-2"
            >
              <Smartphone className="h-4 w-4" />
              {t("home.mobileApp.cta", "Download on the App Store")}
              <ExternalLink className="h-3.5 w-3.5 opacity-70" />
            </a>
          </div>
        </div>
      </section>

      <section className="mt-14 md:mt-20">
        <h2 className="text-sm font-semibold uppercase tracking-widest text-white/40 mb-4 text-center">
          {t("home.otherProducts.title", "Try some of our other products")}
        </h2>
        <div className="grid sm:grid-cols-3 gap-4">
          {OTHER_PRODUCTS.map((product) => (
            <a
              key={product.href}
              href={product.href}
              target="_blank"
              rel="noopener noreferrer"
              className="group relative card hover:border-white/20 transition overflow-hidden"
            >
              <div
                className={`absolute -top-20 -right-20 h-48 w-48 rounded-full blur-3xl bg-gradient-to-br ${product.accent} opacity-60 group-hover:opacity-100 transition`}
              />
              <div className="relative flex flex-col items-start gap-3">
                <div className="h-11 w-11 shrink-0 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center">
                  <product.icon className="h-5 w-5 text-white/90" />
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="text-lg font-semibold">
                      {t(product.titleKey, product.titleKey)}
                    </h3>
                    <ExternalLink className="h-4 w-4 text-white/30 group-hover:text-accent-400 transition" />
                  </div>
                  <p className="text-white/60 text-sm mt-1 leading-relaxed">
                    {t(product.descKey, product.descKey)}
                  </p>
                </div>
              </div>
            </a>
          ))}
        </div>
      </section>
    </div>
  );
}
